import { getLayerProps, getTextLayerProps } from '@/utils/layer-kit';
import {
    IVectorMaterialInfo,
    LayerType,
    MaterialIdsType,
    StretchOptionType,
    VectorType,
    type ILayerProps,
    type ITextLayerProps,
    type IVectorLayerProps,
    type VectorTextInfo,
} from '@/version-types';

export const normalizeBorderRadius = (layerData: IVectorLayerProps) => {
    const { borderRadius, radius } = layerData;

    if (borderRadius) return borderRadius;

    return {
        leftTop: radius,
        rightTop: radius,
        leftBottom: radius,
        rightBottom: radius,
    };
};

/**
 * 生成默认矢量图层 layerData
 * @param override - 覆盖默认值
 * @returns - 矢量图层 layerData
 */
export const createDefaultVectorLayerData = (override: Partial<IVectorLayerProps> = {}) => {
    const DEFAULT_LAYER_DATA: IVectorLayerProps = {
        url: '',
        colors: [],
        naturalWidth: 800,
        naturalHeight: 800,

        vectorType: VectorType.Rect,
        fill: '',
        fillGradient: null,
        stroke: '',
        strokeGradient: null,
        strokeWidth: 0,
        strokeDasharray: '',
        strokeLinecap: '',
        strokeHidden: false,
        radius: 0,
        pickerIndex: null,
        blur: 0,
        stretchOption: StretchOptionType.fill,
        imageTransform: {
            offsetX: 0,
            offsetY: 0,
            rotate: 0,
            scale: 1,
            flipH: false,
            flipV: false,
            widthRatio: 1,
            heightRatio: 1,
        },
        effect: {
            shadow: [],
            reflection: [],
        },
    };

    const layerData = {
        ...DEFAULT_LAYER_DATA,
        ...override,
    };

    const borderRadius = normalizeBorderRadius(layerData);
    layerData.borderRadius = borderRadius;

    return layerData;
};

type CreationOptions = Pick<ILayerProps, 'width' | 'height' | 'left' | 'top'> & {
    materialId?: string;
    data: IVectorLayerProps;
    lockRatio: boolean;
};

/**
 * 根据部分选项（覆盖数据）创建矢量图层
 *
 * @param options - 创建配置选项
 * @returns - 矢量图层
 */
export const createVectorLayer = (options: CreationOptions) => {
    const { width, height, left, top, materialId = '', data, lockRatio } = options;
    if (data.naturalWidth && data.strokeWidth) {
        const ratio = width / data.naturalWidth;
        data.strokeWidth = data.strokeWidth * ratio;
        if (data.strokeWidth < 1) {
            data.strokeWidth = 1;
        }
    }
    console.log('===width,height,data', width, height, data);
    const layer = getLayerProps({
        layerType: LayerType.VECTOR_LAYER,
        data,
    });

    layer.width = width;
    layer.height = height;
    layer.left = left;
    layer.top = top;
    layer.__proactiveWidth = width;
    layer.__proactiveHeight = height;
    layer.extraInfo = {
        ...layer.extraInfo,
        lockRatio,
    };

    if (materialId) {
        const nextMaterialIds = layer.materialIds || {};
        nextMaterialIds[MaterialIdsType.VECTOR_MATERIAL] = [materialId];
        layer.materialIds = nextMaterialIds;
    }

    return layer;
};

/**
 * 将矢量图层转换为文本图层
 *
 * 1. 调用该方法前，请确保矢量图层已经有了文本信息
 * 2. 该方法仅用于渲染层中转换矢量图层数据给文本图层相关渲染组件或出图相关方法
 * 3. 务必要注意字体需要从 runtime 数据中去取子集，保证最终渲染出正确的字体
 *    所以转换时需要传入 runtime 数据
 *
 * @param layer - 矢量图层
 * @returns - 文本图层
 */
export const transformToTextLayer = (layer: ILayerProps) => {
    const layerData = layer.data as IVectorLayerProps;
    const textInfo = layerData.textInfo as VectorTextInfo;

    const textLayerData: ITextLayerProps = {
        text: textInfo.text,
        color: textInfo.color,
        fontSize: textInfo.fontSize,
        fontFamily: textInfo.fontFamily,
        lineSpacing: textInfo.lineSpacing,
        letterSpacing: textInfo.letterSpacing,
        bold: textInfo.bold,
        italic: textInfo.italic,
        lineThrough: textInfo.lineThrough,
        underline: textInfo.underline,
        textAlign: textInfo.textAlign,
        verticalAlign: textInfo.verticalAlign,
        writingMode: textInfo.writingMode,
        listStyle: textInfo.listStyle || '',
        textGlow: [],
        textShadow: [],
        textStroke: [],
        textBackground: [],
        textGradient: null,
    };

    const textLayer = getTextLayerProps({
        id: layer.id,
        type: LayerType.TEXT_LAYER,
        width: layer.width,
        height: layer.height,
        left: layer.left,
        top: layer.top,
        rotate: layer.rotate,
        opacity: textInfo.opacity, // 注意这里的透明度需要从文本信息中获取
        flipH: layerData.imageTransform.flipH, // 注意这里需要从矢量图层的 imageTransform 中获取
        flipV: layerData.imageTransform.flipV,
        data: textLayerData,
    });

    const runtimeData: ILayerProps['__runtime'] = { isInit: true };
    const fontFamilyMap = layer.__runtime?.fontFamilyMap;

    if (fontFamilyMap) runtimeData.fontFamilyMap = fontFamilyMap;

    textLayer.__runtime = runtimeData;
    textLayer.__isOnTextEditing = layer.__isOnTextEditing;
    textLayer.__proactiveWidth = layer.__proactiveWidth;
    textLayer.__proactiveHeight = layer.__proactiveHeight;

    if (textInfo.padding) textLayer.__padding = textInfo.padding;

    return textLayer;
};

/**
 * 获取空图层数据
 */
export function getEmptyVectorLayerProps() {
    return getLayerProps({
        layerType: LayerType.VECTOR_LAYER,
        data: {
            url: '',
            colors: [],
            naturalWidth: 800,
            naturalHeight: 800,

            // maskURL: '',
            // maskType: MaskType.none,

            vectorType: '',
            fill: '',
            fillGradient: null,
            stroke: '',
            strokeGradient: null,
            strokeWidth: 0,
            strokeDasharray: '',
            strokeLinecap: '',
            strokeHidden: false,
            radius: 0,
            pickerIndex: null,
            blur: 0,
            stretchOption: StretchOptionType.fill,
            imageTransform: {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio: 1,
                heightRatio: 1,
            },
            effect: {
                shadow: [],
                reflection: [],
            },
            borderRadius: {
                leftTop: 0,
                rightTop: 0,
                leftBottom: 0,
                rightBottom: 0,
            },
        },
    });
}

/**
 * 获取矢量图层数据
 *
 * @param url - 矢量图链接地址
 * @param colors - 颜色列表
 */
export function getVectorLayerProps(
    {
        url,
        left,
        top,
        width,
        height,
        materialId,
    }: {
        url: string;
        left: number;
        top: number;
        width: number;
        height: number;
        materialId?: string;
    },
    info: IVectorMaterialInfo,
) {
    const layer = getEmptyVectorLayerProps();
    layer.width = width;
    layer.height = height;
    layer.left = left;
    layer.top = top;
    if (materialId) {
        layer.materialIds = layer.materialIds || {};
        layer.materialIds[MaterialIdsType.VECTOR_MATERIAL] = [materialId];
    }
    (layer.data as IVectorLayerProps).url = url;
    layer.data = { ...(layer.data as IVectorLayerProps), ...info };

    if (!info.borderRadius) {
        const { radius } = layer.data;
        layer.data.borderRadius = {
            leftTop: radius,
            rightTop: radius,
            leftBottom: radius,
            rightBottom: radius,
        };
    }
    return layer;
}
